import { Injectable, Signal, signal } from '@angular/core';
import { CookiebotConsentCategories } from './cookiebot-consent-categories.model';
import { LocalStorage } from '@enx/shared/util/enums';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare const Cookiebot: any;

@Injectable({
  providedIn: 'root',
})
export class SharedFeatureConsentStateService {
  private readonly consentState = signal<CookiebotConsentCategories>({
    necessary: false,
    preferences: false,
    statistics: false,
    marketing: false,
  });

  private readonly userResponded = signal<boolean>(false);

  constructor() {
    const stored = localStorage.getItem(LocalStorage.CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiebotConsentCategories;
        this.consentState.set(parsed);
        this.userResponded.set(true);
      } catch {
        localStorage.removeItem(LocalStorage.CONSENT_STORAGE_KEY);
      }
    }

    if (typeof Cookiebot !== 'undefined' && Cookiebot.consents) {
      this.updateConsentStateFromCookiebot();
    }

    window.addEventListener('CookiebotOnAccept', () => {
      this.updateConsentStateFromCookiebot();
    });

    window.addEventListener('CookiebotOnDecline', () => {
      this.updateConsentState({
        necessary: true,
        preferences: false,
        statistics: false,
        marketing: false,
      });
    });
  }

  getConsentSignal(): Signal<CookiebotConsentCategories> {
    return this.consentState;
  }

  hasUserMadeDecision(): boolean {
    return this.userResponded();
  }

  hasNecessaryConsent(): boolean {
    return this.consentState().necessary;
  }

  hasPreferencesConsent(): boolean {
    return this.consentState().preferences;
  }

  hasStatisticsConsent(): boolean {
    return this.consentState().statistics;
  }

  hasMarketingConsent(): boolean {
    return this.consentState().marketing;
  }

  hasAnyConsent(): boolean {
    const consent = this.consentState();
    return consent.necessary || consent.preferences || consent.statistics || consent.marketing;
  }

  private updateConsentStateFromCookiebot(): void {
    const consent = Cookiebot.consent;
    this.updateConsentState({
      necessary: true,
      preferences: consent.preferences === true,
      statistics: consent.statistics === true,
      marketing: consent.marketing === true,
    });
  }

  private updateConsentState(consent: CookiebotConsentCategories): void {
    this.consentState.set(consent);
    this.userResponded.set(true);
    localStorage.setItem(LocalStorage.CONSENT_STORAGE_KEY, JSON.stringify(consent));
  }
}
